import { useSelector } from 'react-redux';
import styled from '@emotion/styled';
import { useRouter } from 'next/router';
import Paper from '@base/Paper/Paper';
import H2 from '@base/H2/H2';
import H5 from '@base/H5/H5';
import P2 from '@base/P2/P2';
import Link from '@base/Link/Link';
import BreadCrumbs from '@base/BreadCrumbs/BreadCrumbs';
import { Spacings } from '@styles/index';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import breadcrumbs from '@utils/breadcrumbs';
import { alwaysEqual } from '@utils/equalFns';
import textHelper from '@utils/textHelper';
import { getImage } from '@utils/imageHelper';
import { getIsGTPGoal } from '@api/selectors/constants';
import useConstants from '@api/hooks/useConstants';
import { getIsSubscribedToOffline } from '@selectors/subscription';

const Header = styled(Paper)`
  margin-bottom: ${Spacings.SPACING_6B};
  border-radius: ${Spacings.SPACING_2B};
  padding: ${Spacings.SPACING_8B} ${Spacings.SPACING_12B};
`;

const HeaderTitle = styled(H2)`
  margin-top: ${Spacings.SPACING_4B};
`;

const SavedItemsContainer = styled.div`
  grid-column: span 18;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-column-gap: ${Spacings.SPACING_4B};
  grid-row-gap: ${Spacings.SPACING_4B};
`;

const SavedItem = styled.div`
  border-radius: ${Spacings.SPACING_2B};
  background-color: var(--color-base-1);
  box-shadow: ${boxShadowGenerator()};
  :hover {
    box-shadow: ${boxShadowGenerator({
      yOffset: Spacings.SPACING_1B,
      blur: Spacings.SPACING_2B
    })};
  }
  transition: box-shadow 0.2s ease-in-out;
`;

const anchorStyle = `
  display: block;
  padding: ${Spacings.SPACING_6B};
`;

const Icon = styled.img`
  user-select: none;
  margin-bottom: ${Spacings.SPACING_4B};
`;

const Title = styled(H5)`
  margin-bottom: ${Spacings.SPACING_2};
  color: var(--color-text-primary);
`;

const SavedCount = styled(P2)`
  color: var(--color-text-secondary);
`;

const Saved = () => {
  const router = useRouter();
  const { goalSlug, goalUID } = router.query;
  const goalTopology = useSelector(
    ({ topologies }) => topologies.data[goalUID],
    alwaysEqual
  );
  const isGTP = useConstants(getIsGTPGoal(goalUID));
  const { isPlatformGoal } = goalTopology;
  const headerTags = (isOfflineSubscription) => {
    const tags = [];
    if (goalTopology) {
      if (goalTopology.isPlatformGoal)
        tags.push(
          ...breadcrumbs.getPlatformBatchTopicsTags(
            goalTopology,
            isOfflineSubscription
          )
        );
      else
        tags.push(
          ...breadcrumbs.getPlusTopicsTags(goalTopology, isOfflineSubscription)
        );
    }
    return tags;
  };
  const savedCounts = useSelector(
    ({ topologies }) => topologies.savedCount[goalUID]
  );

  const onSavedItemClick = () => () => {};

  const savedLessons = savedCounts?.Lesson || 0;
  const savedTests = savedCounts?.Quiz || 0;
  const savedQuestions = savedCounts?.Question || 0;
  const savedNotes = savedCounts?.Notes || 0;
  const isOfflineSubscription = useSelector(getIsSubscribedToOffline(goalUID));
  return (
    <>
      <Header gridColumn="span 18">
        <BreadCrumbs
          tags={headerTags(isOfflineSubscription)}
          onBackClick={breadcrumbs.goBack}
        />
        <HeaderTitle color="var(--color-text-primary)">Saved</HeaderTitle>
      </Header>
      <SavedItemsContainer>
        <SavedItem onClick={onSavedItemClick('lessons')}>
          <Link
            href="/goal/[goalSlug]/[goalUID]/saved/lessons"
            as={`/goal/${goalSlug}/${goalUID}/saved/lessons`}
            anchorStyle={anchorStyle}
          >
            <Icon src={getImage('saved/lessons.svg')} />
            <Title>Lessons</Title>
            <SavedCount>
              {`${textHelper.commas(savedLessons)} saved`}
            </SavedCount>
          </Link>
        </SavedItem>
        {!isPlatformGoal && (
          <>
            <SavedItem onClick={onSavedItemClick('tests')}>
              <Link
                href="/goal/[goalSlug]/[goalUID]/saved/tests"
                as={`/goal/${goalSlug}/${goalUID}/saved/tests`}
                anchorStyle={anchorStyle}
              >
                <Icon src={getImage('saved/tests.svg')} />
                <Title>Tests</Title>
                <SavedCount>{`${textHelper.commas(
                  savedTests
                )} saved`}</SavedCount>
              </Link>
            </SavedItem>
            <SavedItem onClick={onSavedItemClick('questions')}>
              <Link
                href="/goal/[goalSlug]/[goalUID]/saved/questions"
                as={`/goal/${goalSlug}/${goalUID}/saved/questions`}
                anchorStyle={anchorStyle}
              >
                <Icon src={getImage('saved/questions.svg')} />
                <Title>Questions</Title>
                <SavedCount>
                  {`${textHelper.commas(savedQuestions)} saved`}
                </SavedCount>
              </Link>
            </SavedItem>
            {!isGTP && (
              <SavedItem>
                <Link
                  href="/goal/[goalSlug]/[goalUID]/saved/notes"
                  as={`/goal/${goalSlug}/${goalUID}/saved/notes`}
                  anchorStyle={anchorStyle}
                >
                  <Icon src={getImage('saved/myNotes.svg')} />
                  <Title>My Notes</Title>
                  <SavedCount>{`${textHelper.commas(
                    savedNotes
                  )} saved`}</SavedCount>
                </Link>
              </SavedItem>
            )}
          </>
        )}
      </SavedItemsContainer>
    </>
  );
};

export default Saved;
